import i18next from 'i18next'
import type { VariantClassesProps } from 'shared/lib'

export type Permission = {
    id: string
    createdAt: string
    updatedAt: string
    family: string
    key: string
    checkOrganization: boolean
    checkUnit: boolean
    checkSport: boolean
}

export type Role = {
    id: string
    key: RoleKeys
    name: UserRole
    permissions: Permission[]
    createdAt: string
    updatedAt: string
}
export type UserRole = {
    id: number
    role: Role
    createdAt: string
    updatedAt: string
    userId: string
    roleId: string
    organizationId: string
    sportIds: string[]
    unitIds: string[]
    assignedById: string
    key: UserRoleKey
    // Populated fields from API response (getUserByRole)
    user?: {
        id: string
        firstName: string
        lastName: string
        email: string
        imageKey: string | null
        showProfilePhoto?: boolean
        unisportId?: number
    }
    organization?: {
        id: string
        name: string
    }
    sports?: { id: string; name: string }[]
    units?: { id: string; name: string; shortName?: string }[]
    assignedBy?: { id: string; firstName?: string; lastName?: string }
}

export type UserRoles = Array<UserRole>

export enum UserRoleKey {
    SUPER_ADMIN = 'Super Admin',
    ORGANIZATION_ADMIN = 'Admin Organizacije',
    UNIT_ADMIN = 'Admin Sastavnice',
    TECHNICAL_DELEGATE = 'Tehnički Delegat',
    TECHNICIAN = 'Tehničar',
    NOTETAKER = 'Zapisničar',
    TEAM_ADMIN = 'Voditelj Ekipe',
    RECREATION_ADMIN = 'Admin Rekreacija',
    RECREATION_TRAINER = 'Trener Rekreacija',
    CMS_ADMIN = 'CMS Admin',
    CMS_EDITOR = 'CMS Editor',
    ADMIN = 'Admin',
}

export enum RoleKeys {
    SUPER_ADMIN = 'super-admin',
    ORGANIZATION_ADMIN = 'organization-admin',
    UNIT_ADMIN = 'unit-admin',
    TECHNICAL_DELEGATE = 'technical-delegate',
    TECHNICIAN = 'technician',
    NOTETAKER = 'notetaker',
    TEAM_ADMIN = 'team-admin',
    RECREATION_ADMIN = 'recreation-admin',
    RECREATION_TRAINER = 'recreation-trainer',
    CMS_ADMIN = 'cms-admin',
    CMS_EDITOR = 'cms-editor',
    ADMIN = 'admin',
}

const getUserRoles = (): VariantClassesProps => ({
    'super-admin': i18next.t('roles:roles.super-admin'),
    'organization-admin': i18next.t('roles:roles.organization-admin'),
    'unit-admin': i18next.t('roles:roles.unit-admin'),
    'technical-delegate': i18next.t('roles:roles.technical-delegate'),
    technician: i18next.t('roles:roles.technician'),
    notetaker: i18next.t('roles:roles.notetaker'),
    'team-admin': i18next.t('roles:roles.team-admin'),
    'recreation-admin': i18next.t('roles:roles.recreation-admin'),
    'recreation-trainer': i18next.t('roles:roles.recreation-trainer'),
    'cms-admin': i18next.t('roles:roles.cms-admin'),
    'cms-editor': i18next.t('roles:roles.cms-editor'),
    admin: i18next.t('roles:roles.admin'),
})

export const userRoles: VariantClassesProps = new Proxy(
    {} as VariantClassesProps,
    {
        get: (_target, key: string) => getUserRoles()[key],
        ownKeys: () => Object.keys(getUserRoles()),
        getOwnPropertyDescriptor: (_target, key: string) => ({
            value: getUserRoles()[key as string],
            enumerable: true,
            configurable: true,
        }),
    }
)

export type RequestAddPermissionBody = {
    userId: string | number
    roleId: string
    organizationId?: string
    sportIds?: string[]
    unitIds?: string[]
}

export type RequestEditPermissionBody = {
    userRoleId: string
    data: {
        organizationId?: string
        sportIds?: string[]
        unitIds?: string[]
    }
}

export interface UserRoleOptions {
    id: number
    key: UserRoleKey
    name: string
}

export interface TransformedRole {
    canAssignRoles: []
    id: number
    createAt: string
    updatedAt: string
    key: string
    name: string
    permissions: Permission[]
    transformedLabel: string
}

export type RolesAndPermissionsData = {
    role: string
    roleName: string
    organization: {
        name: string
    }
    unitIds: string
    sportIds: string
    assignedById: string
    assignedBy?: { id: string; firstName?: string; lastName?: string }
}

export type RolesAndPermissionFormData = {
    unit: ''
    organization: ''
    sport: ''
}
